import React, { ChangeEvent, FC, MouseEvent, useEffect } from 'react';
import { Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TablePaginationActions } from './TablePaginationOptions';
import { CollapsibleRow } from '../CollapsibleRow';
import { CustomTable } from '../../../../models/CustomTable';

interface PaginationTableProps extends CustomTable {
  currentPage?: number;
  setCurrentPage?: (page: number) => void;
  totalRowsCount?: number;
  rowsPerPage?: number;
  setRowsPerPage?: (rows: number) => void;
  isLazyTable?: boolean;
  toTop?: React.MutableRefObject<HTMLDivElement | null>;
}

const defaultProps = {
  currentPage: 0,
  setCurrentPage: undefined,
  totalRowsCount: 0,
  isLazyTable: false,
  rowsPerPage: 5,
  setRowsPerPage: undefined,
  toTop: undefined,
};

export const PaginationTable: FC<PaginationTableProps> = ({
  isAutoResizeEmptyPaginationRows = false,
  className,
  tableBodyCells,
  tableHead,
  isCollapsibleButton,
  isVerticalAlignCells,
  currentPage,
  setCurrentPage,
  totalRowsCount,
  isLazyTable,
  rowsPerPage,
  setRowsPerPage,
  toTop,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = React.useState(currentPage || 0);
  const [rowsPerPageRemove, setRowsPerPageRemove] = React.useState(rowsPerPage || 5);

  const rowsCount = totalRowsCount || (tableBodyCells ? tableBodyCells.length : 0);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPageRemove - rowsCount) : 0;

  useEffect(() => {
    if (currentPage !== undefined) {
      setPage(currentPage);
      if (toTop && rowsPerPageRemove > 5 && toTop.current) {
        toTop.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentPage]);

  const handleChangePage = (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
    if (setCurrentPage) {
      setCurrentPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPageRemove(parseInt(event.target.value, 10));
    if (setRowsPerPage) {
      setRowsPerPage(parseInt(event.target.value, 10));
    }
    setPage(0);
    if (setCurrentPage) {
      setCurrentPage(0);
    }
  };

  return (
    <Table className={`custom-table ${className || ''}`}>
      <TableHead>
        {rowsPerPageRemove > 5 ? (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            className="custom-pagination"
            colSpan={4}
            count={rowsCount}
            rowsPerPage={rowsPerPageRemove}
            page={page}
            labelRowsPerPage={t('table.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => {
              return ` ${from} - ${to} ${t('table.of')} ${count}`;
            }}
            SelectProps={{
              inputProps: {
                'aria-label': 'rows per page',
              },
              native: true,
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
          />
        ) : null}

        <TableRow>
          {tableHead.map((el) => {
            return <TableCell key={el}>{el}</TableCell>;
          })}
        </TableRow>
      </TableHead>
      <TableBody>
        {tableBodyCells?.length &&
          (!(rowsPerPageRemove > 0) || isLazyTable
            ? tableBodyCells
            : tableBodyCells.slice(page * rowsPerPageRemove, page * rowsPerPageRemove + rowsPerPageRemove)
          ).map((el) => {
            if (el.history) {
              return (
                <CollapsibleRow
                  {...el}
                  key={el.id}
                  isCollapsibleButton={isCollapsibleButton}
                  isVerticalAlignCells={isVerticalAlignCells}
                />
              );
            }
            return (
              <TableRow key={el.id} className={el.className}>
                {el.label && <TableCell>{el.label}</TableCell>}
                {el.bodyCells.map((cell, index) => {
                  return (
                    <TableCell
                      key={`${cell}${index + 1}`}
                      className={`${el.isDisabled ? 'disabled' : ''} ${
                        isVerticalAlignCells ? 'vertical-align-cells' : ''
                      }`}
                    >
                      {cell}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        {emptyRows > 0 && isAutoResizeEmptyPaginationRows && (
          <TableRow style={{ height: 53 * emptyRows }}>
            <TableCell colSpan={6} />
          </TableRow>
        )}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            className="custom-pagination"
            colSpan={4}
            count={rowsCount}
            rowsPerPage={rowsPerPageRemove}
            page={page}
            labelRowsPerPage={t('table.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => {
              return ` ${from} - ${to} ${t('table.of')} ${count}`;
            }}
            SelectProps={{
              inputProps: {
                'aria-label': 'rows per page',
              },
              native: true,
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
          />
        </TableRow>
      </TableFooter>
    </Table>
  );
};

PaginationTable.defaultProps = defaultProps;
